import type { ReactNode } from 'react';
import { PlainLink } from 'components/link';
import styles from './slot-tearsheet.module.scss';

/**
 * ── EVERY LINK THE TEARSHEET RENDERS GOES THROUGH HERE (UAT 9, 9 Sep) ────────────────────
 *
 * Andrew lost his place mid-UAT: a link navigated the whole page away, and getting back
 * meant reopening the package, the calculation and the slot, then re-expanding whatever he
 * had open. The tearsheet is a MODAL over a view with its own state — an in-place navigation
 * discards all of it.
 *
 * ── WHY A COMPONENT RATHER THAN `target` ON THE TWO EXISTING ANCHORS ─────────────────────
 *
 * There were exactly two anchors when this was written, so two attributes would have been
 * the smaller change. It is the wrong change: UAT 4 adds a type-curve link and UAT 8 adds
 * the UWI and unit links, and the whole point is that a new link INHERITS this instead of
 * its author remembering. A convention nobody can forget beats a convention written down.
 *
 * ── `rel` IS NOT OPTIONAL WITH `target="_blank"` ─────────────────────────────────────────
 *
 * `noopener` denies the opened page a `window.opener` handle back to this one (which it could
 * otherwise use to navigate it); `noreferrer` withholds the referrer. Modern browsers imply
 * noopener for target=_blank, but not all of them and not in every embedding, so it is stated
 * rather than assumed.
 *
 * ── WHAT THIS DOES NOT DO ────────────────────────────────────────────────────────────────
 *
 * It does not decide WHETHER to link. A null href is not a link, and the caller already has
 * to distinguish "no href" from "the lookup ran and found nothing" — see
 * `missingRecordNote` and node_binder's three-outcome design. This renders an anchor once
 * the caller has decided there is one.
 *
 * ── AND IT NO LONGER WRITES THE ANCHOR ITSELF (Jason, PR #7465) ─────────────────────────
 *
 * It used to emit `<a target="_blank" rel="noopener noreferrer">` by hand, which is exactly
 * what `PlainLink` (components/link) already bundles behind `openInNewTab` — and
 * eval3-new/case-management.tsx:498 renders `<PlainLink className={styles.scenarioLink} href
 * openInNewTab>`, the same three props, one directory over. That pairing is the thing worth
 * centralising: shipping `target="_blank"` without the `rel` is the mistake, and it is now
 * impossible here.
 *
 * THE WRAPPER STAYS, and this is the part the review did not weigh: its value is the
 * DEFAULT className, not the anchor. All five call sites pass only `href` and children, so a
 * new link inherits the tearsheet's style and the new-tab behaviour without its author
 * remembering either. Substituting PlainLink at each call site would move three props into
 * five places and lose precisely the property this exists for. So it delegates now instead
 * of duplicating.
 *
 * ONE DELIBERATE RENDERING CHANGE: PlainLink also applies its own `link` class, whose
 * `:visited` rule is more specific than `.scenarioLink`'s flat `color`. Visited tearsheet
 * links therefore go purple, as every other link in the app already does. Stated because it
 * is a visible change, not a silent side effect of closing a review comment.
 *
 * @param {object} props - The href, the visible content, and an optional class override.
 * @return {JSX.Element} The anchor.
 */
export function TearsheetLink({
  href,
  children,
  className = styles.scenarioLink,
  title,
}: {
  href: string;
  children: ReactNode;
  /** Defaults to the shared link style; pass one only to place a link differently. */
  className?: string;
  title?: string;
}) {
  return (
    <PlainLink className={className} href={href} openInNewTab title={title}>
      {children}
    </PlainLink>
  );
}
